import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, CircularProgress, DialogActions, DialogContent, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { concat, debounce, filter, find, get, includes } from 'lodash';
import { isSupported as isBrowserSupported } from 'twilio-video';

import { appointmentTypesResource, facilitiesResource, providersResource } from '@lib/resources';

import DialogHeader from 'components/DialogHeader';
import { useStyles } from 'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep/SessionInfoStep.useStyles';
import {
  FORM_NAME,
  SESSION_TYPE,
} from 'components/DialogVideoOnDemandSessionCreation/DialogVideoOnDemandSessionCreation';
import SelectField from 'components/SelectField';
import TelehealthBrowserNotSupportedBanner from 'components/TelehealthBrowserNotSupportedBanner';

interface SessionInfoStepProps {
  classes: any;
  formProps: any;
  isProvider: boolean;
  isAppointmentTypeEnabled: boolean;
  onClose: () => void;
}

const FACILITY_RESULT_KEY = 'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep/facilities';
const PROVIDER_RESULT_KEY = 'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep/providers';
const APPOINTMENT_TYPES_RESULT_KEY =
  'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep/appointmentTypes';

const LIMIT_PARAM = 500;

const SessionInfoStep: React.FC<SessionInfoStepProps> = ({
  classes,
  formProps,
  isProvider,
  isAppointmentTypeEnabled,
  onClose,
}) => {
  const {
    errors,
    isSubmitting,
    setFieldTouched,
    setFieldValue,
    touched,
    values: { facility, sessionType, provider, appointmentType },
  } = formProps;
  const btnRef = useRef<HTMLButtonElement>();
  const title =
    !isProvider && sessionType === SESSION_TYPE.telehealth
      ? 'Great! Let’s get more information.'
      : 'Invite patient to telehealth session';
  const facilityLabel = isProvider ? 'What is your facility?' : 'What is the facility?';

  const [isFiltering, setIsFiltering] = useState(false);
  const [facilityInputValue, setFacilityInputValue] = useState('');
  const [providerInputValue, setProviderInputValue] = useState('');
  const [appointmentTypeInputValue, setAppointmentTypeInputValue] = useState('');

  const { items: filteredFacilities } = useSelector(facilitiesResource.makeSelectPage(FACILITY_RESULT_KEY));
  const { items: filteredProviders } = useSelector(providersResource.makeSelectPage(PROVIDER_RESULT_KEY));
  const { items: filteredAppointmentTypes } = useSelector(
    appointmentTypesResource.makeSelectPage(APPOINTMENT_TYPES_RESULT_KEY),
  );

  const facilities = useSelector(facilitiesResource.selectEntitiesObj);
  const providers = useSelector(providersResource.selectEntitiesObj);
  const appointmentTypes = useSelector(appointmentTypesResource.selectEntitiesObj);

  const facilityOptions =
    facilities[facility] && !find(filteredFacilities, { id: facility })
      ? concat(filteredFacilities, [facilities[facility]])
      : filteredFacilities;
  const providerOptions =
    providers[provider] && !find(filteredProviders, { id: provider })
      ? concat(filteredProviders, [providers[provider]])
      : filteredProviders;
  const appointmentTypeOptions =
    appointmentTypes[appointmentType] && !find(filteredAppointmentTypes, { id: appointmentType })
      ? concat(filteredAppointmentTypes, [appointmentTypes[appointmentType]])
      : filteredAppointmentTypes;
  // the backend will set the default appt type value when there's only one option
  const isAppointmentTypeFieldVisible =
    isAppointmentTypeEnabled && appointmentTypeOptions && appointmentTypeOptions.length > 1;

  const dispatch = useDispatch();

  const loadFacilities = useCallback(
    (name: string = '') => {
      let params: any = {
        page: 1,
        limit: LIMIT_PARAM,
        name,
      };

      if (isProvider) {
        params = { ...params, userGroups: true };
        setIsFiltering(name !== '');
      }

      dispatch(
        facilitiesResource.fetchPage({
          params,
          meta: {
            resultKey: FACILITY_RESULT_KEY,
          },
        }),
      );
    },
    [dispatch, isProvider],
  );

  const loadProviders = useCallback(
    (name: string = '') => {
      dispatch(
        providersResource.fetchPage({
          params: {
            page: 1,
            limit: LIMIT_PARAM,
            name,
            'settings.telehealth.enabled': true,
          },
          meta: {
            resultKey: PROVIDER_RESULT_KEY,
          },
        }),
      );
    },
    [dispatch],
  );

  const loadAppointmentTypes = useCallback(
    (name: string = '') => {
      dispatch(
        appointmentTypesResource.fetchPage({
          params: {
            page: 1,
            limit: LIMIT_PARAM,
            name,
            modality: 'telehealth',
          },
          meta: {
            resultKey: APPOINTMENT_TYPES_RESULT_KEY,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleProviderInputChange = (value: string) => {
    setProviderInputValue(value);
    if (
      (providerInputValue !== value && (value !== '' || (providerInputValue === '' && !provider))) ||
      (value === '' && providerInputValue !== '')
    ) {
      loadProviders(value);
    }
  };

  useEffect(() => {
    loadFacilities();
    loadProviders();
    loadAppointmentTypes();
  }, [loadFacilities, loadProviders, loadAppointmentTypes]);

  // this code is checking if we have ONLY ONE facility and ApptType is NOT enabled.
  // If so we hide facility field and after select the default facility we submit the form automatically
  // We can add the same logic for when there is only one appointment type too.
  const hideContent = isProvider && !isFiltering && facilityOptions.length === 1 && !isAppointmentTypeEnabled;
  const isToAutoSubmit = hideContent && !isSubmitting;
  const defaultFacility = facilityOptions[0] ? (facilityOptions[0] as any)._id : null;

  useEffect(() => {
    if (isToAutoSubmit) {
      if (!facility && defaultFacility) {
        setFieldValue('facility', defaultFacility);
      } else if (btnRef && btnRef.current) {
        btnRef.current.click();
      }
    }
  }, [facility, defaultFacility, isToAutoSubmit, setFieldValue]);

  return (
    <>
      <DialogHeader title={title} onRequestClose={onClose} />
      <TelehealthBrowserNotSupportedBanner />
      <DialogContent className={classnames(classes.content, { [classes.hidden]: hideContent })}>
        {hideContent && <CircularProgress />}
        <Grid className={classnames({ [classes.hidden]: hideContent })} container spacing={16}>
          <Grid item xs={12}>
            <SelectField
              // @ts-ignore
              error={Boolean(touched.facility && errors.facility)}
              filterOption={null}
              floatingLabel
              helperText={touched.facility && errors.facility}
              label={facilityLabel}
              onBlur={() => setFieldTouched('facility', true)}
              onChange={(value: any) => setFieldValue('facility', value)}
              onInputChange={(value: any) => {
                const filterOptions = (option: any) =>
                  includes(get(option, 'name', get(option, 'label', '')).toLowerCase(), value.toLowerCase());

                if (
                  (facilityInputValue !== value &&
                    filter(facilityOptions, filterOptions).length === 0 &&
                    (value !== '' || (facilityInputValue === '' && !facility))) ||
                  (value === '' && facilityInputValue !== '')
                ) {
                  loadFacilities(value);
                }

                setFacilityInputValue(value);
              }}
              InputProps={{ 'data-lh-id': 'telehealth-on-demand-session-facility' }}
              options={facilityOptions}
              searchable
              showRequiredMark
              value={facility}
            />
          </Grid>
          {!isProvider && (
            <Grid item xs={12}>
              <SelectField
                // @ts-ignore
                error={Boolean(touched.provider && errors.provider)}
                filterOption={null}
                floatingLabel
                helperText={touched.provider && errors.provider}
                label="Who is the provider?"
                onBlur={() => setFieldTouched('provider', true)}
                onChange={(value: any) => setFieldValue('provider', value)}
                onInputChange={debounce(handleProviderInputChange, 300)}
                options={providerOptions}
                searchable
                showRequiredMark
                value={provider}
                InputProps={{ 'data-lh-id': 'telehealth-on-demand-session-provider' }}
              />
            </Grid>
          )}
          {isAppointmentTypeFieldVisible && (
            <Grid item xs={12}>
              <SelectField
                // @ts-ignore
                error={Boolean(touched.appointmentType && errors.appointmentType)}
                filterOption={null}
                floatingLabel
                helperText={touched.appointmentType && errors.appointmentType}
                label="Select the appointment type"
                onBlur={() => setFieldTouched('appointmentType', true)}
                onChange={(value: any) => setFieldValue('appointmentType', value)}
                onInputChange={(value: any) => {
                  const filterOptions = (option: any) =>
                    includes(get(option, 'name', get(option, 'label', '')).toLowerCase(), value.toLowerCase());

                  if (
                    (appointmentTypeInputValue !== value &&
                      filter(appointmentTypeOptions, filterOptions).length === 0 &&
                      (value !== '' || (appointmentTypeInputValue === '' && !appointmentType))) ||
                    (value === '' && appointmentTypeInputValue !== '')
                  ) {
                    loadAppointmentTypes(value);
                  }

                  setAppointmentTypeInputValue(value);
                }}
                options={appointmentTypeOptions}
                searchable
                showRequiredMark
                value={appointmentType}
                InputProps={{ 'data-lh-id': 'telehealth-on-demand-session-appointment-type' }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classnames({ [classes.hidden]: hideContent })}>
        <Button
          color="primary"
          onClick={onClose}
          variant="text"
          data-lh-id="telehealth-on-demand-session-cancel-button"
        >
          Cancel
        </Button>
        <Button
          buttonRef={btnRef}
          color="primary"
          disabled={isSubmitting || (!isBrowserSupported && isProvider)}
          form={FORM_NAME}
          type="submit"
          variant="contained"
          data-lh-id="telehealth-on-demand-session-confirm-button"
        >
          Confirm and send invite
        </Button>
      </DialogActions>
    </>
  );
};

export default withStyles(useStyles)(SessionInfoStep);
